/**
 * TSPL Command Generator & Direct Print Dispatcher for Thermal Barcode Label Printers
 * Mendukung Xprinter XP-420B, XP-365B, TSC, Gprinter, Postek, dan semua printer TSPL/TSPL2.
 * Resolusi standar: 203 DPI (8 dots/mm).
 */

import { sendRawWindowsBytes, getSavedWindowsPrinter, listWindowsPrinters, setSavedWindowsPrinter, isWindowsPrintBridgeAvailable } from "./windowsPrintBridge";
import { sendRawSerialBytes, getSavedSerialPrinter, isWebSerialSupported } from "./serialPrintManager";

/**
 * Format nama produk aman ASCII dan multi-line wrapping untuk label tinggi
 */
function formatProductNameLines(prod, maxCharsPerLine = 20, isMultiLineAllowed = false) {
  let rawName = String(prod.name || prod.parentName || "Produk").trim();
  if (prod.variantName && !rawName.toLowerCase().includes(prod.variantName.toLowerCase())) {
    const vStr = prod.variantName.trim();
    const formattedV = vStr.startsWith("(") ? vStr : `(${vStr})`;
    rawName = `${prod.parentName || rawName} ${formattedV}`;
  }

  // Bersihkan karakter non-ASCII (seperti tanda petik unicode atau elipsis) agar tidak menjadi simbol aneh seperti ôç di printer TSPL
  rawName = rawName
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2026]/g, "...")
    .replace(/[^\x20-\x7E]/g, ""); // hanya karakter ASCII printable

  if (!isMultiLineAllowed || rawName.length <= maxCharsPerLine) {
    if (rawName.length > maxCharsPerLine) {
      return [rawName.slice(0, maxCharsPerLine - 2) + ".."];
    }
    return [rawName];
  }

  // Potong kata demi kata untuk multi-baris rapi
  const words = rawName.split(" ");
  const lines = [];
  let currentLine = "";

  for (const word of words) {
    if ((currentLine + (currentLine ? " " : "") + word).length <= maxCharsPerLine) {
      currentLine += (currentLine ? " " : "") + word;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
      if (lines.length >= 2) break; // Maksimal 2 baris
    }
  }
  if (currentLine && lines.length < 2) {
    lines.push(currentLine);
  }

  // Jika baris ke-2 masih melebihi batas, potong dengan aman
  if (lines.length > 1 && lines[1].length > maxCharsPerLine) {
    lines[1] = lines[1].slice(0, maxCharsPerLine - 2) + "..";
  }

  return lines;
}

/**
 * Membangun perintah TSPL mentah dalam bentuk Uint8Array dengan layout seimbang vertikal dan horizontal
 */
export function generateTsplForBarcodeLabels({
  thermalRows,
  layout,
  fields,
  density = 12,
  speed = 4,
}) {
  const paperW = Number(layout.paperW || 108);
  const cardH = Number(layout.cardH || 15);
  const cardW = Number(layout.cardW || 33);
  const gapY = Number(layout.gapY || 2);
  const gapX = Number(layout.gapX || 2);
  const marginX = Number(layout.marginX || 2.5);
  const marginY = Number(layout.marginY || 0);

  const cardWidthDots = Math.round(cardW * 8);   // 33mm = 264 dots
  const cardHeightDots = Math.round(cardH * 8);  // 15mm = 120 dots, 25mm = 200 dots

  const commands = [];

  // 1. Inisialisasi Ukuran & Sensor Kertas
  commands.push(`SIZE ${paperW} mm, ${cardH} mm`);
  commands.push(`GAP ${gapY} mm, 0 mm`);
  commands.push(`DIRECTION 1,0`);
  commands.push(`REFERENCE 0,0`);
  commands.push(`DENSITY ${density}`);
  commands.push(`SPEED ${speed}`);
  commands.push(`SET PEEL OFF`);
  commands.push(`SET CUTTER OFF`);
  commands.push(`SET TEAR ON`);

  // 2. Loop Setiap Baris Stiker
  for (const row of thermalRows) {
    commands.push(`CLS`);

    row.forEach((prod, colIdx) => {
      if (!prod) return;

      const colLeftMm = marginX + colIdx * (cardW + gapX);
      const startX = Math.round(colLeftMm * 8); // Posisi X awal label
      const startY = Math.round(marginY * 8);    // Posisi Y awal label

      const isTallCard = cardH >= 20; // 20mm ke atas memiliki ruang vertikal lega
      const safeWidthDots = Math.max(40, cardWidthDots - 36);
      const maxCharsPerLine = Math.max(6, Math.floor(safeWidthDots / 9.5));

      const nameLines = fields.show_product_name !== false
        ? formatProductNameLines(prod, maxCharsPerLine, isTallCard)
        : [];

      // Posisi Y awal dinamis agar tidak menempel mepet ke atas
      let currentY = startY + (cardH <= 16 ? 6 : cardH <= 22 ? 10 : 14);

      // 1. Nama Produk (Rata Tengah / Centered)
      if (nameLines.length > 0) {
        nameLines.forEach((line) => {
          const nameWidthDots = Math.round(line.length * 9.5);
          const nameX = startX + Math.max(10, Math.round((cardWidthDots - nameWidthDots) / 2));
          commands.push(`TEXT ${nameX}, ${currentY}, "1", 0, 1, 1, "${escapeTsplString(line)}"`);
          currentY += 15;
        });
        currentY += cardH <= 16 ? 2 : 6;
      }

      // 2. Harga & SKU (Rata Tengah / Centered)
      const priceText = fields.show_price !== false ? `Rp${Number(prod.price || 0).toLocaleString("id-ID")}` : "";
      const skuText = fields.show_sku && prod.sku ? String(prod.sku).trim() : "";

      if (priceText || skuText) {
        let lineText = "";
        if (priceText && skuText) {
          lineText = `${priceText}  ${skuText.slice(0, 8)}`;
        } else if (priceText) {
          lineText = priceText;
        } else {
          lineText = `SKU ${skuText.slice(0, 12)}`;
        }

        const lineWidthDots = Math.round(lineText.length * 9.5);
        const priceX = startX + Math.max(10, Math.round((cardWidthDots - lineWidthDots) / 2));

        commands.push(`TEXT ${priceX}, ${currentY}, "1", 0, 1, 1, "${escapeTsplString(lineText)}"`);
        currentY += 15 + (cardH <= 16 ? 3 : 8);
      }

      // 3. Barcode (Rata Tengah, Mengisi Area Bawah Proporsional)
      if (fields.show_barcode !== false && prod.barcode) {
        const barcodeVal = String(prod.barcode).trim();
        const isEan13 = /^\d{13}$/.test(barcodeVal);
        const barcodeType = isEan13 ? "EAN13" : "128";
        
        let narrow = 2;
        let approxWidth = isEan13 ? 190 : (barcodeVal.length * 11 + 35) * narrow;
        if (approxWidth > cardWidthDots - 24) {
          narrow = 1;
          approxWidth = isEan13 ? 95 : (barcodeVal.length * 11 + 35) * narrow;
        }

        const barcodeX = startX + Math.max(8, Math.round((cardWidthDots - approxWidth) / 2));
        
        // Sisa tinggi vertikal yang tersedia hingga batas bawah label
        const remainingH = (startY + cardHeightDots) - currentY - 24;
        const barcodeHeight = Math.max(34, Math.min(cardH <= 16 ? 44 : 75, remainingH));

        // BARCODE X, Y, "Type", Height, HumanReadable (2=Centered bawah), Rotation (0), Narrow (1/2), Wide (2), "Content"
        commands.push(`BARCODE ${barcodeX}, ${currentY}, "${barcodeType}", ${barcodeHeight}, 2, 0, ${narrow}, 2, "${barcodeVal}"`);
      }
    });

    // Cetak 1 baris
    commands.push(`PRINT 1,1`);
  }

  const fullScript = commands.join("\r\n") + "\r\n";
  const encoder = new TextEncoder();
  return encoder.encode(fullScript);
}

function escapeTsplString(str) {
  return String(str || "").replace(/"/g, "'").replace(/[\r\n]+/g, " ");
}

/**
 * Mendapatkan status printer direct yang tersedia
 */
export async function getDirectPrinterStatus() {
  const savedWindows = getSavedWindowsPrinter();
  const savedSerial = getSavedSerialPrinter();

  let bridgeAvailable = false;
  if (savedWindows) {
    bridgeAvailable = await isWindowsPrintBridgeAvailable();
  }

  return {
    windows: savedWindows ? { name: savedWindows.name, ready: bridgeAvailable } : null,
    serial: savedSerial ? { name: savedSerial.name || "Port COM", ready: true } : null,
    hasAny: !!((savedWindows && bridgeAvailable) || savedSerial),
  };
}

/**
 * Eksekusi cetak langsung TSPL ke perangkat hardware
 */
export async function executeTsplPrint({ thermalRows, layout, fields }) {
  if (!thermalRows || thermalRows.length === 0) {
    throw new Error("Tidak ada label yang dipilih untuk dicetak.");
  }

  const tsplBytes = generateTsplForBarcodeLabels({ thermalRows, layout, fields });

  // Prioritas Utama: Windows Print Spooler Helper
  const savedWindows = getSavedWindowsPrinter();
  if (savedWindows) {
    try {
      await sendRawWindowsBytes(tsplBytes);
      return { success: true, method: "windows", name: savedWindows.name };
    } catch (winErr) {
      console.warn("Gagal cetak via Windows Spooler:", winErr);
      throw new Error(`Gagal mengirim ke Windows Spooler (${winErr.message || winErr})`);
    }
  }

  // Prioritas Cadangan: Serial / COM Port
  const savedSerial = getSavedSerialPrinter();
  if (savedSerial && isWebSerialSupported()) {
    try {
      await sendRawSerialBytes(tsplBytes);
      return { success: true, method: "serial", name: savedSerial.name || "Port COM" };
    } catch (serErr) {
      console.warn("Gagal cetak via Port COM:", serErr);
      throw new Error(`Gagal mengirim ke Port COM (${serErr.message || serErr})`);
    }
  }

  throw new Error("NO_PRINTER_CONFIGURED");
}
